//@flow
import React, { Component } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { FormattedMessage } from 'react-intl';
import getter from 'dibs-object-getter';
import { injectIntl } from 'react-intl';

import { SocialShare } from '../components/SocialShare/SocialShare';
import Email from 'dibs-icons/exports/Email';
import Pen from 'dibs-icons/exports/Pen';

import { ContactDesignerModal } from './ContactDesignerModal';

import { profileEmailMutation } from '../mutations/profileEmail';
import { ProfileFollowRenderer } from './ProfileFollowRenderer';

import { Input } from 'dibs-elements/exports/Input';
import { Spinner } from 'dibs-elements/exports/Spinner';

import { logError } from '../../helpers';
import styles from './ProfileContactInfo.scss';

import type { ProfileContactInfo_tradeFirm as TradeFirmType } from './__generated__/ProfileContactInfo_tradeFirm.graphql';
import type { intlShape } from 'react-intl';
import type { RelayProp } from 'react-relay';

type Props = {
    tradeFirm: ?TradeFirmType,
    isEditMode: boolean,
    relay?: RelayProp,
    intl: intlShape,
};

type State = {
    isEditing: boolean,
    isSaving: boolean,
    hasSaveError: boolean,
    email: string,
    shareModalOpen: boolean,
};

export class ProfileContactInfoComponent extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            isEditing: false,
            isSaving: false,
            hasSaveError: false,
            shareModalOpen: false,
            email:
                getter(props.tradeFirm)
                    .at('publicProfile')
                    .value('email') || '',
        };
    }

    startEditing = () => {
        if (this.props.isEditMode) {
            this.setState({ isEditing: true });
        }
    };

    saveEmail = (event: SyntheticInputEvent<HTMLInputElement>) => {
        const email = event.target.value || '';
        this.setState({ isSaving: true, email, hasSaveError: false });

        // Verify email
        if (email.indexOf('@') <= 0 || email.indexOf('.', email.indexOf('@')) < 0) {
            this.setState({ hasSaveError: true, isSaving: false });
            return;
        }

        const firmId = getter(this.props.tradeFirm).value('serviceId');

        if (firmId && this.props.relay) {
            profileEmailMutation(this.props.relay.environment, { firmId, email })
                .then(() => {
                    this.setState({ isSaving: false, isEditing: false });
                    return;
                })
                .catch(e => {
                    logError(e, 'Error(s) on saving contact email:');
                    this.setState({ isSaving: false, hasSaveError: true });
                    return;
                });
            return;
        }

        logError('Could not save -- no firm ID loaded');
        this.setState({ isSaving: false, hasSaveError: true });
    };

    render() {
        if (this.props.isEditMode && this.state.isEditing) {
            return (
                <div className={styles.wrapper}>
                    <Input
                        dataTn="header-email-edit"
                        hasError={this.state.hasSaveError}
                        disabled={this.state.isSaving}
                        rightDecorator={this.state.isSaving ? <Spinner size="tiny" /> : ''}
                        onBlur={this.saveEmail}
                        defaultValue={this.state.email}
                        className={styles.inputWrapper}
                        type="email"
                        autoFocus
                    />
                </div>
            );
        } else if (this.props.isEditMode) {
            return (
                <div className={styles.editWrapper} onClick={this.startEditing}>
                    <Email className={styles.iconWrapper} />
                    <span>
                        {this.state.email || (
                            <FormattedMessage
                                id="abt.designProfile.header.addEmail"
                                defaultMessage="Add Your Email"
                            />
                        )}
                    </span>
                    <Pen className={styles.penWrapper} />
                </div>
            );
        }
        const firmName = getter(this.props.tradeFirm).value('name');
        const photoPath = getter(this.props.tradeFirm)
            .at('publicProfile')
            .value('profilePhoto');
        const urlLabel = getter(this.props.tradeFirm)
            .at('publicProfile')
            .value('urlLabel');
        return (
            <div className={styles.liveWrapper}>
                <ProfileFollowRenderer firmUrlLabel={urlLabel} />
                <ContactDesignerModal tradeFirm={(this.props.tradeFirm: ?Object)} />
                <div className={styles.shareWrapper}>
                    <SocialShare firmName={firmName} photoPath={photoPath} urlLabel={urlLabel} />
                </div>
            </div>
        );
    }
}

export const ProfileContactInfo = createFragmentContainer(injectIntl(ProfileContactInfoComponent), {
    tradeFirm: graphql`
        fragment ProfileContactInfo_tradeFirm on TradeFirmType {
            ...ContactDesignerModal_tradeFirm
            serviceId
            name
            publicProfile {
                profilePhoto
                urlLabel
                email
            }
        }
    `,
});
